import React, { useState, useEffect } from 'react';
import { enhancedApi } from '../services/api';
import { DepartmentManagement } from './DepartmentManagement';
import { UnitManagement } from './UnitManagement';
import { TeamManagement } from './TeamManagement';
import { SettingsManagement } from './SettingsManagement';
import { UserModal } from '../components/UserModal';
import { 
  Users, 
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  Shield,
  Activity,
  Database,
  BarChart3,
  Building2,
  MapPin,
  Settings
} from 'lucide-react';

interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  department_id: number;
  department_name: string;
  unit_id: number;
  unit_name: string;
  is_active: boolean;
}

interface AdminDashboardProps {
  defaultTab?: 'users' | 'departments' | 'units' | 'teams' | 'settings';
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ defaultTab = 'users' }) => {
  const [activeTab, setActiveTab] = useState<'users' | 'departments' | 'units' | 'teams' | 'settings'>(defaultTab);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userModalMode, setUserModalMode] = useState<'create' | 'edit'>('create');

  useEffect(() => {
    if (activeTab === 'users') {
      fetchUsers();
    }
  }, [activeTab]);

  useEffect(() => {
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await enhancedApi.users.getUsers();
      if (response.success) {
        // Handle both array and object response structures
        let usersData = [];
        if (Array.isArray(response.data)) {
          usersData = response.data;
        } else if (response.data && Array.isArray(response.data.users)) {
          usersData = response.data.users;
        }
        setUsers(usersData);
        console.log('Fetched users:', usersData);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleUserDelete = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await enhancedApi.users.deleteUser(selectedUser.id);
      fetchUsers();
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setUserModalMode('create');
    setShowUserModal(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setUserModalMode('edit');
    setShowUserModal(true);
  };

  const handleUserModalSuccess = () => {
    fetchUsers();
  };

  const handleUserModalClose = () => {
    setShowUserModal(false);
    setSelectedUser(null);
  };

  return (
    <div className="space-y-8">
      {/* Enhanced Header */}
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
              <p className="text-blue-100">Manage users, settings, and system configuration</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="flex border-b">
          <button
            className={`px-6 py-4 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('users')}
          >
            <Users className="h-4 w-4 inline mr-2" />
            User Management
          </button>
          <button
            className={`px-6 py-4 font-medium text-sm ${
              activeTab === 'departments'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('departments')}
          >
            <Building2 className="h-4 w-4 inline mr-2" />
            Department Management
          </button>
              <button
                className={`px-6 py-4 font-medium text-sm ${
                  activeTab === 'units'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('units')}
              >
                <MapPin className="h-4 w-4 inline mr-2" />
                Unit Management
              </button>
              <button
                className={`px-6 py-4 font-medium text-sm ${
                  activeTab === 'teams'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('teams')}
              >
                <Users className="h-4 w-4 inline mr-2" />
                Team Management
              </button>
              <button
                className={`px-6 py-4 font-medium text-sm ${
                  activeTab === 'settings'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('settings')}
              >
                <Settings className="h-4 w-4 inline mr-2" />
                Settings
              </button>
        </div>
      </div>

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* User Management Sub-header */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 px-8 py-6 border-b border-gray-100">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">User Management</h3>
                  <p className="text-gray-600">Manage system users, roles, and permissions</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h4 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                <Users className="h-5 w-5 text-blue-600" />
                <span>System Users</span>
              </h4>
            <button 
              className="btn btn-primary"
              onClick={handleCreateUser}
            >
              <Plus className="h-4 w-4" />
              Add User
            </button>
          </div>

          {/* User Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
              <span className="ml-3 text-gray-600">Loading users...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Department</th>
                    <th>Unit</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? users.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                            {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                          </div>
                          <div className="ml-3">
                            <div className="font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${
                          user.role === 'superuser' ? 'badge-error' :
                          user.role === 'reviewer' ? 'badge-warning' :
                          'badge-info'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center">
                          <Database className="h-4 w-4 text-gray-400 mr-2" />
                          {user.department_name}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center">
                          <Activity className="h-4 w-4 text-gray-400 mr-2" />
                          {user.unit_name}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${user.is_active ? 'badge-success' : 'badge-error'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <button className="btn btn-sm btn-outline">
                            <Eye className="h-4 w-4" />
                          </button>
                          <button 
                            className="btn btn-sm btn-primary"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            className="btn btn-sm btn-error"
                            onClick={() => handleUserDelete(user)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={6} className="text-center py-8 text-gray-500">
                        {loading ? 'Loading users...' : 'No users found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Department Management Tab */}
      {activeTab === 'departments' && (
        <DepartmentManagement />
      )}

          {/* Unit Management Tab */}
          {activeTab === 'units' && (
            <UnitManagement />
          )}

          {/* Team Management Tab */}
          {activeTab === 'teams' && (
            <TeamManagement />
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <SettingsManagement />
          )}

          {/* User Modal */}
          <UserModal
            isOpen={showUserModal}
            onClose={handleUserModalClose}
            onSuccess={handleUserModalSuccess}
            user={selectedUser}
            mode={userModalMode}
          />

          {/* Delete User Modal */}
          {showDeleteModal && selectedUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
                    <p className="text-sm text-gray-500">This action cannot be undone</p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to permanently delete this user?
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="text-sm">
                      <div className="mb-2">
                        <span className="font-medium text-gray-700">Name:</span>
                        <p className="text-gray-900">{selectedUser.first_name} {selectedUser.last_name}</p>
                      </div>
                      <div className="mb-2">
                        <span className="font-medium text-gray-700">Email:</span>
                        <p className="text-gray-900">{selectedUser.email}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Role:</span>
                        <p className="text-gray-900 capitalize">{selectedUser.role}</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-red-600 mt-3">
                    ⚠️ This will permanently remove the user and all their associated data.
                  </p>
                </div>
                
                <div className="flex justify-end gap-3">
                  <button 
                    onClick={() => {
                      setShowDeleteModal(false);
                      setSelectedUser(null);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={confirmDeleteUser}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4 mr-2 inline" />
                    Delete User
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    };